import { useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext.jsx';
import { ROLES } from '../../utils/roles.js';
import './HelpDrawer.css';

const TABS = [
  { key: 'welcome', label: 'Welcome', icon: 'fa-solid fa-house' },
  { key: 'modules', label: 'Modules', icon: 'fa-solid fa-th-large' },
  { key: 'howto', label: 'How To', icon: 'fa-solid fa-list-check' },
  { key: 'faq', label: 'FAQ', icon: 'fa-solid fa-circle-question' },
];

const WELCOME_SUBTABS = [
  { key: 'start', label: 'Getting Started' },
  { key: 'roles', label: 'Roles & Access' },
  { key: 'tour', label: 'Quick Tour' },
];

const HOWTO_SUBTABS = [
  { key: 'orders', label: 'Pull Orders' },
  { key: 'production', label: 'Production' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'transfers', label: 'Transfers' },
  { key: 'recipes', label: 'Recipes & BOM' },
];

const FAQ_SUBTABS = [
  { key: 'general', label: 'General' },
  { key: 'orders', label: 'Orders' },
  { key: 'stock', label: 'Stock & Alerts' },
];

/* ── Content ─────────────────────────────────────────── */

function WelcomeContent({ sub }) {
  if (sub === 'start') return (
    <div className="hd-prose">
      <h3>Welcome to KitchenOS</h3>
      <p>KitchenOS is the operating system for modern commissary kitchens. It connects every brand, outlet, and kitchen station so your team always has the right information at the right time.</p>

      <h4>Your Journey in 6 Steps</h4>
      <ol>
        <li><strong>Set up master data</strong> — brands, outlets, kitchen stations, and users. Your Super Admin does this once in <em>System Setup</em>.</li>
        <li><strong>Load raw material items</strong> — ingredients and SKUs with safety-stock thresholds and standard costs.</li>
        <li><strong>Build your recipes</strong> — define batch sizes, ingredient quantities, and units. KitchenOS calculates cost automatically.</li>
        <li><strong>Outlet managers submit pull orders</strong> — before the brand cut-off time each day. Commissary supervisors acknowledge and plan production.</li>
        <li><strong>Schedule production runs</strong> — book kitchen slots, record batch yields, and dispatch finished goods.</li>
        <li><strong>Monitor stock and alerts</strong> — low-stock and expiry alerts surface before you run out. Raise reorder requests directly from the alert.</li>
      </ol>

      <h4>What's on your dashboard?</h4>
      <p>Your landing page is the <strong>Order Desk</strong> — a live list of pull orders in every status. Use the sidebar to navigate to production, inventory, transfers, and reports.</p>
    </div>
  );

  if (sub === 'roles') return (
    <div className="hd-prose">
      <h3>Roles &amp; Access</h3>
      <p>Every user is assigned one role. Access is enforced on both the front-end (menus) and back-end (API).</p>

      <div className="hd-role-table">
        <div className="hd-role-row hd-role-header">
          <span>Role</span><span>Module Access</span>
        </div>
        {[
          { role: 'Super Admin', access: 'Everything — system setup, all reports, all modules', color: '#ef4444' },
          { role: 'Finance', access: 'System setup, recipes, BOM costing, cost allocation reports, activity log', color: '#f59e0b' },
          { role: 'Brand Manager', access: 'Pull orders, consolidated demand dashboard', color: '#60a5fa' },
          { role: 'Commissary Supervisor', access: 'Pull orders, production schedule, stock ledger, transfers, inventory alerts', color: '#a78bfa' },
          { role: 'Outlet Manager', access: 'Pull order entry, inbound transfer receipt', color: '#34d399' },
          { role: 'Procurement Staff', access: 'Stock ledger, inventory alerts, reorder requests', color: '#fb923c' },
        ].map(({ role, access, color }) => (
          <div className="hd-role-row" key={role}>
            <span><span className="hd-role-badge" style={{ background: color + '22', color }}>{role}</span></span>
            <span>{access}</span>
          </div>
        ))}
      </div>

      <p className="hd-note"><i className="fa-solid fa-circle-info" /> To change a user's role or outlet assignment, contact your Super Admin.</p>
    </div>
  );

  if (sub === 'tour') return (
    <div className="hd-prose">
      <h3>Quick Tour</h3>
      <div className="hd-feature-grid">
        {[
          { icon: 'fa-solid fa-cart-flatbed', title: 'Order Desk', desc: 'Submit, track, and acknowledge pull orders across all outlets and brands.', color: '#60a5fa' },
          { icon: 'fa-solid fa-calendar-check', title: 'Production Scheduler', desc: 'Book kitchen slots, plan production runs, and record batch yields.', color: '#a78bfa' },
          { icon: 'fa-solid fa-boxes-stacking', title: 'Stock Ledger', desc: 'Log receipts, view live inventory levels, and trigger reorder requests.', color: '#34d399' },
          { icon: 'fa-solid fa-truck', title: 'Transfers', desc: 'Dispatch finished goods to outlets, record received quantities, flag discrepancies.', color: '#fb923c' },
          { icon: 'fa-solid fa-flask', title: 'Recipes & BOM', desc: 'Versioned recipes with auto-calculated batch costs and variance tracking.', color: '#f59e0b' },
          { icon: 'fa-solid fa-chart-bar', title: 'Cost Reports', desc: 'Standard vs actual cost per brand, variance analysis, full audit log.', color: '#fb7185' },
        ].map(({ icon, title, desc, color }) => (
          <div className="hd-feature-card" key={title}>
            <div className="hd-feature-icon" style={{ background: color + '1a', color }}><i className={icon} /></div>
            <div>
              <div className="hd-feature-title">{title}</div>
              <div className="hd-feature-desc">{desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return null;
}

function ModulesContent() {
  return (
    <div className="hd-prose">
      <h3>KitchenOS Modules</h3>
      {[
        {
          icon: 'fa-solid fa-store', title: 'System Administration & Master Data', color: '#60a5fa',
          desc: 'Configure brands, outlets, kitchen stations, users, and roles. Set cut-off rules per brand. All reference data used by every other module lives here.',
          roles: 'Super Admin, Finance',
        },
        {
          icon: 'fa-solid fa-cart-flatbed', title: 'Brand Pull Order Management', color: '#34d399',
          desc: 'Outlet managers submit pull orders before cut-off. Commissary supervisors acknowledge or reject. Track orders from Submitted → Acknowledged → In Production → Dispatched → Received.',
          roles: 'All roles',
        },
        {
          icon: 'fa-solid fa-calendar-check', title: 'Production Schedule & Kitchen Slot Booking', color: '#a78bfa',
          desc: 'Book kitchen slots against real capacity. Conflict detection prevents double-booking. Record batch yields to close the loop between demand and production.',
          roles: 'Commissary Supervisor, Super Admin',
        },
        {
          icon: 'fa-solid fa-flask', title: 'Recipe & Bill of Materials Repository', color: '#f59e0b',
          desc: 'Versioned recipes with ingredients, quantities, units, and standard costs. KitchenOS calculates batch cost and tracks variance against actual spend.',
          roles: 'Super Admin, Finance',
        },
        {
          icon: 'fa-solid fa-layer-group', title: 'Raw Material Inventory', color: '#fb923c',
          desc: 'Monitor raw material stock across all locations. Log receipts, track expiry dates, and raise reorder requests. Low-stock and near-expiry alerts keep you ahead of shortages.',
          roles: 'Commissary Supervisor, Procurement Staff',
        },
        {
          icon: 'fa-solid fa-truck', title: 'Transfer & Delivery Tracker', color: '#fb7185',
          desc: 'Dispatch finished goods from commissary to outlets. Receiving outlets confirm quantities. Discrepancies are captured and logged for audit.',
          roles: 'Commissary Supervisor, Outlet Manager',
        },
        {
          icon: 'fa-solid fa-chart-bar', title: 'Brand Cost Allocation Report', color: '#c084fc',
          desc: 'See standard cost vs actual cost per brand. Variance analysis down to SKU level. Tied to the audit log for full traceability.',
          roles: 'Super Admin, Finance',
        },
        {
          icon: 'fa-solid fa-bell', title: 'Inventory Alert Digest & Settings', color: '#2dd4bf',
          desc: 'Configure alert thresholds and notification preferences per location. Digest view consolidates low-stock and expiry alerts across all items.',
          roles: 'Commissary Supervisor, Procurement Staff',
        },
        {
          icon: 'fa-solid fa-clipboard-list', title: 'System Activity Log', color: '#94a3b8',
          desc: 'Immutable audit trail of every action — master data changes, order status transitions, stock adjustments. Filterable by module, actor, and date.',
          roles: 'Super Admin, Finance',
        },
      ].map(({ icon, title, desc, roles, color }) => (
        <div className="hd-module-card" key={title}>
          <div className="hd-module-icon" style={{ background: color + '1a', color }}><i className={icon} /></div>
          <div className="hd-module-body">
            <div className="hd-module-title">{title}</div>
            <div className="hd-module-desc">{desc}</div>
            <div className="hd-module-roles"><i className="fa-solid fa-user-shield" /> {roles}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function HowToContent({ sub }) {
  const guides = {
    orders: [
      { q: 'How do I submit a pull order?', a: 'Go to Order Desk → click New Pull Order → select your outlet and delivery date → add items with quantities → click Submit. Orders must be submitted before the brand cut-off time.' },
      { q: 'Can I edit a submitted order?', a: 'Yes — Outlet Managers can edit submitted orders before the cut-off time. After cut-off, only a Commissary Supervisor can make changes.' },
      { q: 'What statuses does a pull order go through?', a: 'Submitted → Acknowledged (or Rejected with reason) → In Production → Dispatched → Received. Each transition is time-stamped in the audit log.' },
      { q: 'What happens after cut-off?', a: 'No new orders can be submitted for that delivery date. Commissary supervisors can still edit or reject orders that are already in Submitted status.' },
      { q: 'How do I acknowledge or reject an order?', a: 'Open the order in Order Desk → click Acknowledge to confirm or Reject to decline with a reason. Outlet managers see the updated status immediately.' },
    ],
    production: [
      { q: 'How do I book a kitchen slot?', a: 'Open Production Schedule → click New Production Run → select a date and kitchen station → choose the items to produce → set quantities → click Book Slot. Conflicts are highlighted automatically.' },
      { q: 'How do I record production completion?', a: 'Open the scheduled run → enter the actual batch yield per item → click Complete. Stock levels are updated and the order status advances to In Production.' },
      { q: 'What is conflict detection?', a: 'KitchenOS prevents two production runs from being booked to the same kitchen station at the same time. If a slot is taken, it shows in red on the calendar.' },
    ],
    inventory: [
      { q: 'How do I log a stock receipt?', a: 'Go to Raw Material Receipt & Stock Ledger → click Log Receipt → select the item and supplier → enter quantity, unit cost, lot number, and expiry date → confirm. Stock is updated immediately.' },
      { q: 'How do alerts work?', a: 'KitchenOS compares current stock against the safety-stock threshold set on each item. If stock falls below threshold OR a batch is expiring within 7 days, an alert appears in the Inventory Alert Digest.' },
      { q: 'How do I raise a reorder request?', a: 'Click the Reorder button on a low-stock alert → confirm the item and suggested quantity → submit. Procurement Staff see the request in their queue.' },
      { q: 'How do I adjust stock?', a: 'Go to Stock Ledger → click Adjust Stock → enter the item, new quantity, reason, and reference → save. Every adjustment is logged in the activity log.' },
    ],
    transfers: [
      { q: 'How do I dispatch a transfer?', a: 'Go to Transfer Dispatch & Receipt → click New Transfer → select destination outlet → add items and quantities → enter driver details → click Dispatch.' },
      { q: 'How does the receiving outlet confirm?', a: 'The outlet manager opens Transfers → finds the inbound transfer → enters received quantities (noting any discrepancies) → clicks Confirm Receipt.' },
      { q: 'What if the received quantity differs from dispatched?', a: 'Enter the actual received quantity. KitchenOS records the discrepancy in the transfer record and flags it for the supervisor.' },
    ],
    recipes: [
      { q: 'How do I create a recipe?', a: 'Go to Master Recipe & BOM → click New Recipe → add ingredients with quantities and units → set the batch size → save. KitchenOS calculates the total batch cost from ingredient standard prices.' },
      { q: 'What is a recipe version?', a: 'Each time you edit and save a recipe, a new version is created (v1.0, v1.1, …). Previous versions are preserved for cost history and audit. Only the Active version is used in production costing.' },
      { q: 'How does batch cost calculate?', a: 'KitchenOS multiplies each ingredient quantity by its standard unit cost and sums the lines. The total is the standard batch cost. Variance is tracked when actual spend differs.' },
    ],
  };

  const items = guides[sub] || [];
  return (
    <div className="hd-prose">
      <h3>How To — {HOWTO_SUBTABS.find(t => t.key === sub)?.label}</h3>
      {items.map((item, i) => (
        <div className="hd-guide-step" key={i}>
          <div className="hd-guide-q"><i className="fa-solid fa-chevron-right" /> {item.q}</div>
          <div className="hd-guide-a">{item.a}</div>
        </div>
      ))}
    </div>
  );
}

function FaqContent({ sub }) {
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen(p => ({ ...p, [k]: !p[k] }));

  const faqs = {
    general: [
      { q: 'What if I see a "Forbidden" page?', a: 'Your role does not have access to that page. Contact your Super Admin to review your role or outlet assignment.' },
      { q: 'How do I reset my password?', a: 'KitchenOS does not support self-service password reset. Contact your Super Admin to reset your password.' },
      { q: 'Can I belong to multiple outlets?', a: 'Yes. A Super Admin can assign a user to multiple outlets. Your access then covers all assigned outlets.' },
      { q: 'Where do I see who did what?', a: 'Go to System Activity Log (Super Admin / Finance only). Every action is logged with actor, timestamp, module, and details.' },
      { q: 'Is my data real-time?', a: 'Yes. Stock levels, order statuses, and production records update immediately when a transaction is saved.' },
    ],
    orders: [
      { q: 'What if I miss the cut-off?', a: 'You cannot submit a new order for a date after cut-off has passed. Contact your Commissary Supervisor to arrange an exception or emergency order.' },
      { q: 'Can a rejected order be re-submitted?', a: 'No — once rejected, the order cannot be re-submitted. Create a new pull order instead.' },
      { q: 'Why can\'t I see orders from other outlets?', a: 'Outlet Managers see only their own outlet\'s orders. Commissary Supervisors and Brand Managers see all orders within their assigned scope.' },
      { q: 'What does "In Production" mean?', a: 'The commissary has started producing the items. The order will advance to Dispatched once finished goods leave the commissary.' },
    ],
    stock: [
      { q: 'Why is my stock showing as negative?', a: 'This can happen if a production run or dispatch is recorded without a corresponding receipt. Check the stock ledger log and raise a stock adjustment with a corrective quantity.' },
      { q: 'How is the safety-stock threshold set?', a: 'Thresholds are set on each item in System Setup (Super Admin / Finance). Contact your Super Admin to update the threshold.' },
      { q: 'Can I dismiss an alert without acting on it?', a: 'Alerts clear automatically once the stock level rises above the threshold or the near-expiry window passes. There is no manual dismiss.' },
      { q: 'What does "Expiring Soon" mean?', a: 'A batch whose expiry date falls within 7 days is flagged as Expiring Soon. Use it before it expires or log a waste adjustment.' },
    ],
  };

  const items = faqs[sub] || [];
  return (
    <div className="hd-prose">
      <h3>FAQ — {FAQ_SUBTABS.find(t => t.key === sub)?.label}</h3>
      {items.map((item, i) => {
        const k = `${sub}-${i}`;
        return (
          <div className="hd-faq-item" key={k}>
            <button className={`hd-faq-q${open[k] ? ' hd-faq-q--open' : ''}`} onClick={() => toggle(k)}>
              <span>{item.q}</span>
              <i className={`fa-solid ${open[k] ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
            {open[k] && <div className="hd-faq-a">{item.a}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Main component ───────────────────────────────────── */

export default function HelpDrawer({ open, onClose }) {
  const [tab, setTab] = useState('welcome');
  const [sub, setSub] = useState('start');

  const subtabs = tab === 'welcome' ? WELCOME_SUBTABS
    : tab === 'howto' ? HOWTO_SUBTABS
    : tab === 'faq' ? FAQ_SUBTABS
    : null;

  // Reset sub-tab when primary tab changes
  const handleTab = (key) => {
    setTab(key);
    if (key === 'welcome') setSub('start');
    else if (key === 'howto') setSub('orders');
    else if (key === 'faq') setSub('general');
    else setSub(null);
  };

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {open && <div className="hd-backdrop" onClick={onClose} />}
      <aside className={`hd-drawer${open ? ' hd-drawer--open' : ''}`} aria-label="Help and documentation" data-testid="help-drawer">
        {/* Header */}
        <div className="hd-header">
          <div className="hd-header-left">
            <i className="fa-solid fa-circle-question hd-header-icon" />
            <div>
              <div className="hd-header-eyebrow">KitchenOS</div>
              <div className="hd-header-title">Help &amp; Documentation</div>
            </div>
          </div>
          <button className="hd-close" onClick={onClose} aria-label="Close help" data-testid="help-close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Primary tab bar */}
        <div className="hd-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`hd-tab${tab === t.key ? ' hd-tab--active' : ''}`}
              onClick={() => handleTab(t.key)}
              data-testid={`help-tab-${t.key}`}
            >
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Sub-tab bar */}
        {subtabs && (
          <div className="hd-subtabs">
            {subtabs.map(s => (
              <button
                key={s.key}
                className={`hd-subtab${sub === s.key ? ' hd-subtab--active' : ''}`}
                onClick={() => setSub(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="hd-body">
          {tab === 'welcome' && <WelcomeContent sub={sub} />}
          {tab === 'modules' && <ModulesContent />}
          {tab === 'howto' && <HowToContent sub={sub} />}
          {tab === 'faq' && <FaqContent sub={sub} />}
        </div>

        {/* Footer */}
        <div className="hd-footer">
          <i className="fa-solid fa-headset" />
          Need more help? Contact your system administrator.
        </div>
      </aside>
    </>
  );
}
